/**
 * Pure helpers for the Agent-page channel tabs
 * (`ai-docs/lexide-agent-channel-tabs-impl-plan.md` §3.2). Kept out of the
 * components + hooks so the load-bearing logic (scope→media mapping, page
 * merge, null-bucket filter) is unit-tested in the node env rather than behind
 * a React mount.
 */
import type { LxChatReadMediaParams } from "@/types/gateway";
import type { ChannelMessage } from "@/api/lx-channels";

/** The scope-derivation half of `LxChatReadMediaParams` — the caller adds `name`. */
export type MediaScopeParams = Pick<
  LxChatReadMediaParams,
  "scope" | "clientId" | "matterId" | "caseId"
>;

const LX_PREFIX = "agent:main:lx:";

/**
 * Map a channel row's raw LX session key (`msg.scope`) to the scope params
 * `lx.chat_read_media` expects, or `null` when this key has no media mapping.
 *
 * ## Why casing MUST be preserved verbatim
 * The server REBUILDS the session key from these params
 * (`resolveSessionKey` → `buildCaseSessionKey`/`buildLxSessionKey`) and keys the
 * JSONL + durable media dir on that exact string. A re-cased id keys a
 * DIFFERENT file and the media read comes back NOT_FOUND. So: match the fixed
 * segments (`agent:main:lx:`, `case:`, `client:`, `matter:` — always lowercase
 * as the builder writes them) exactly, and copy id substrings UNTOUCHED. We
 * deliberately do NOT mirror the server's `parseLxSessionKey` lowercasing —
 * that parser serves comparison, not key reconstruction, and would corrupt a
 * mixed-case id here.
 *
 * ## What maps
 * Only the two grammars `lx.chat_read_media` can resolve — an IM turn only ever
 * binds to a global or case scope:
 *   - `agent:main:lx:global`                              → `{ scope: "global" }`
 *   - `agent:main:lx:case:{caseId}`                       → `{ caseId }`
 *   - `agent:main:lx:client:{c}:matter:{m}:case:{caseId}` → `{ clientId, matterId, caseId }`
 * Everything else (research / bare client / matter / sub-case doc·pet·proc
 * layers / garbage) → `null`, and the chip degrades to name-only (no fetch).
 */
export function channelScopeToMediaParams(scope: string): MediaScopeParams | null {
  if (typeof scope !== "string" || !scope.startsWith(LX_PREFIX)) return null;
  const parts = scope.slice(LX_PREFIX.length).split(":");

  // agent:main:lx:global
  if (parts.length === 1 && parts[0] === "global") return { scope: "global" };

  // agent:main:lx:case:{caseId}  (D-6 caseId-only key)
  if (parts.length === 2 && parts[0] === "case" && parts[1]) {
    return { caseId: parts[1] };
  }

  // agent:main:lx:client:{c}:matter:{m}:case:{caseId}  (4-level key)
  if (
    parts.length === 6 &&
    parts[0] === "client" &&
    parts[1] &&
    parts[2] === "matter" &&
    parts[3] &&
    parts[4] === "case" &&
    parts[5]
  ) {
    return { clientId: parts[1], matterId: parts[3], caseId: parts[5] };
  }

  return null;
}

/**
 * The `caseId` a channel row belongs to, or `null` (global scope, or any key
 * that carries no case). Feeds the conversation-row scope chips through the
 * existing case-label index (`buildCaseLabelIndex`/`resolveCaseLabel`).
 */
export function channelScopeToCaseId(scope: string): string | null {
  return channelScopeToMediaParams(scope)?.caseId ?? null;
}

/** Stable identity of a stored turn-side: `(runId, role)` is globally unique in
 *  the conversation store (a runId records at most once, and a turn appends at
 *  most one user + one assistant row). */
function rowKey(m: ChannelMessage): string {
  return `${m.runId} ${m.role}`;
}

/**
 * Prepend an older page ahead of the rows already held, dropping any row we
 * already have (defence against a `<=` cursor drift on a same-ts page
 * boundary). Both inputs are oldest-first; the result stays oldest-first.
 */
export function mergeOlderPage(
  current: ChannelMessage[],
  older: ChannelMessage[],
): ChannelMessage[] {
  if (older.length === 0) return current;
  const held = new Set(current.map(rowKey));
  const fresh = older.filter((m) => !held.has(rowKey(m)));
  return [...fresh, ...current];
}

/** The stash-miss bucket: rows with no peer id (`channels.conversations`
 *  groups them under `peer:null`; `channels.messages` cannot filter to them —
 *  see the impl plan §7 — so the thread view filters client-side). */
export function filterNullPeerRows(messages: ChannelMessage[]): ChannelMessage[] {
  return messages.filter((m) => !m.peer);
}
